import React, {useEffect, useState} from "react";
import {SubclassDisplayChart} from "./components/SubclassDisplayChart";
import {useTranslation} from "react-i18next";
import axiosInstance from "../../api/axios";

const SubclassDisplay = ({
  id,
  currentWasteItem,
  dayvalue,
  language,
  message2,
  selectedButton,
  totalwasteproduced,
  colorwaste,
  percentagechange,
  TotalWasteWeight,
  datevalue,
  currentWasteItem_,
  wasteweight: wasteweightProp  // Accept weight prop
}) => {
  const { t, i18n } = useTranslation();
  const [totalwasteproduceddata, setTotalWasteProducedData] = useState('');
  const [wasteweight, setWasteWeight] = useState(0);
  const [colorwastechart, setColorWasteChart] = useState();
  const [percentagechangewastechart, setPercentageChangeWasteChart] = useState();
  const [season, setSeason] = useState('summer');

  // Get current season based on date
  const getCurrentSeason = () => {
    const month = new Date().getMonth() + 1; // 1-12
    if (month >= 3 && month <= 5) return 'spring';
    if (month >= 6 && month <= 8) return 'summer';
    if (month >= 9 && month <= 11) return 'autumn';
    return 'winter';
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const binIdentifiers = Array.isArray(id) ? id : [id];
        const bins = binIdentifiers.join(',');

        // Enhanced timeRange mapping with debugging
        const timeRangeMap = {
          'day': 'Day',
          'week': 'Week',
          'month': 'Month',
          '3months': '3Month',
          '3 month': '3Month',
          '3-month': '3Month',
          '6months': '6Month',
          '6 month': '6Month',
          '6-month': '6Month',
          'year': 'Year'
        };

        // Normalize the dayvalue to handle different formats
        const normalizedDayValue = dayvalue.toLowerCase().replace(/\s+/g, '').replace(/-/g, '');
        const timeRange = timeRangeMap[normalizedDayValue] || timeRangeMap[dayvalue] || 'Month';
        const currentSeason = getCurrentSeason();

        console.log('API Request Params:', {
          bins,
          timeRange,
          season: currentSeason,
          originalDayValue: dayvalue,
          normalizedDayValue
        });

        // Use ONLY the others_page_waste_composition API for weight data
        const compositionResponse = await axiosInstance.get('/others_page_waste_composition', {
          params: {
            bins: bins,
            timeRange: timeRange,
            season: currentSeason
          }
        });

        console.log('Composition API Response:', compositionResponse.data);

        // Set the total weight from composition API
        if (compositionResponse.data && compositionResponse.data.total !== undefined) {
          setWasteWeight(Number(compositionResponse.data.total.toFixed(2)));
        }

        // For objects count, percentage change, and color - fetch from wastebarchart
        const binResponse = await axiosInstance.get('/get_bins');
        const validBins = binIdentifiers
          .map(identifier => binResponse.data.find(bin => bin.unique_identifier === identifier))
          .filter(bin => bin);

        if (validBins.length > 0) {
          const binIds = validBins.map(bin => bin.id).join(',');
          let timePeriod = dayvalue.toLowerCase().replace(/\s+/g, '');
          
          const wasteResponse = await axiosInstance.get(
            `/wastebarchart/${binIds}/${timePeriod}/`,
            { params: { wasteitem: currentWasteItem_ } }
          );

          if (wasteResponse.data) {
            setPercentageChangeWasteChart(parseFloat(wasteResponse.data?.percentage_change || 0).toFixed(2));
            setColorWasteChart(wasteResponse.data?.color || 'gray');
            setTotalWasteProducedData(wasteResponse.data?.objects || 0);
          }
        }

      } catch (error) {
        console.error('Fetch error:', error);
        // Set defaults
        setPercentageChangeWasteChart(0);
        setColorWasteChart('gray');
        setTotalWasteProducedData(0);
        
        // If we have wasteweightProp as fallback, use it
        if (wasteweightProp !== undefined && wasteweightProp !== null) {
          setWasteWeight(Number(wasteweightProp.toFixed(2)));
        } else {
          setWasteWeight(0);
        }
      }
    };

    fetchData();
  }, [currentWasteItem_, id, dayvalue, wasteweightProp]);

  // Helper function to get translated time period
  const getTranslatedTimePeriod = (button) => {
    const normalizedButton = button?.toLowerCase();
    switch (normalizedButton) {
      case 'year':
        return t('year');
      case '6months':
      case '6 month':
      case '6-month':
        return t('6months');
      case '3months':
      case '3 month':
      case '3-month':
        return t('3months');
      case 'month':
        return t('month');
      case 'week':
        return t('week');
      case 'day':
        return t('day');
      case 'custom':
        return t('custom');
      default:
        return button;
    }
  };

  // Helper function to translate day/month names
  const translateDateElement = (text) => {
    if (!text) return text;

    const lowerText = text.toLowerCase();

    // Days of the week
    const dayTranslations = {
      'monday': t('monday'),
      'tuesday': t('tuesday'),
      'wednesday': t('wednesday'),
      'thursday': t('thursday'),
      'friday': t('friday'),
      'saturday': t('saturday'),
      'sunday': t('sunday')
    };

    // Months
    const monthTranslations = {
      'january': t('january'),
      'february': t('february'),
      'march': t('march'),
      'april': t('april'),
      'may': t('may'),
      'june': t('june'),
      'july': t('july'),
      'august': t('august'),
      'september': t('september'),
      'october': t('october'),
      'november': t('november'),
      'december': t('december')
    };

    // Waste types
    const wasteTypeTranslations = {
      'organic': t('organic'),
      'pmd': t('pmd'),
      'paper': t('paper'),
      'residual': t('residual')
    };

    return dayTranslations[lowerText] ||
           monthTranslations[lowerText] ||
           wasteTypeTranslations[lowerText] ||
           text;
  };

  // Get translated message
  const getTranslatedMessage = () => {
    const translatedPeriod = getTranslatedTimePeriod(selectedButton);
    return `${t('totalwastecomparison')} ${translatedPeriod}`;
  };

  return (
    <SubclassDisplayChart
      id={Array.isArray(id) ? id.join(',') : id}
      wasteitem={currentWasteItem}
      dayvalue={dayvalue}
      datevalue={datevalue}
      selectedButton={selectedButton}
      totalwasteproduceddata={totalwasteproduceddata}
      wasteweight={wasteweight}
      colorwastechart={colorwastechart}
      percentagechangewastechart={percentagechangewastechart}
      season={season}
    />
  );
};

export default SubclassDisplay;